import type { Palette, PainPoint } from './types';

type GapProps = { palette: Palette; painPoints: PainPoint[] };

export function GapSection({ palette, painPoints }: GapProps) {
  return (
    <section
      className="relative overflow-hidden px-6 py-20 lg:px-12"
      // A lighter background to contrast with the dark Hero, but keeping the "Sage" tint
      style={{ backgroundColor: '#F0F4F2' }}
    >
      {/* --- BACKGROUND BLOBS FOR DEPTH --- */}
      <div 
        className="absolute left-0 top-1/2 h-[500px] w-[500px] -translate-y-1/2 rounded-full opacity-40 blur-[80px]"
        style={{ background: `${palette.sage}40` }}
      />
      <div 
        className="absolute right-0 bottom-0 h-[400px] w-[400px] rounded-full opacity-30 blur-[60px]"
        style={{ background: `${palette.clay}20` }}
      />

      <div className="relative mx-auto max-w-7xl">
        {/* Main Card Container with Depth */}
        <div className="grid gap-12 rounded-[40px] border border-white/60 bg-white/60 p-8 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.1)] backdrop-blur-xl lg:grid-cols-2 lg:p-12 lg:items-center">
          
          {/* Left Column: The Emotional Connection */}
          <div className="flex flex-col justify-center space-y-6">
            
            {/* Styled "We Understand" Pill */}
            <div className="flex items-center gap-3">
               <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100/50 text-emerald-700">
                 <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                 </svg>
               </div>
               <p className="text-sm font-bold uppercase tracking-[0.2em]" style={{ color: palette.forest }}>
                 We Understand
               </p>
            </div>
            
            <h2 className="text-3xl font-bold leading-tight text-slate-900 sm:text-4xl">
              Life is busy. <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-emerald-800">
                Your home shouldn't be another job.
              </span>
            </h2>
            
            <p className="text-lg leading-relaxed text-slate-600">
              We get it. Time is limited, and your to-do list is already a mile long. 
              The last thing you need is the stress of chasing contractors or researching technical specs.
            </p>

            {/* Featured Quote Box - Pops out now */}
            <div className="relative mt-4 overflow-hidden rounded-2xl bg-amber-50 p-6 border border-amber-100 shadow-sm">
              <div className="absolute -right-4 -top-4 text-9xl font-serif text-amber-100 select-none">”</div>
              <p className="relative z-10 text-base font-medium italic text-slate-800">
                "We know the struggle of trying to do it all. That’s why we exist—to handle the heavy lifting so you can just come home and relax."
              </p>
            </div>
          </div>

          {/* Right Column: The Burdens We Remove */}
          <div className="relative">
            {/* Visual Header for the list */}
            <div className="mb-6 flex items-center justify-between border-b border-slate-200 pb-2">
               <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                 The Mental Load
               </p>
               <p className="text-xs font-bold uppercase tracking-wider text-emerald-600">
                 We Handle It ↓
               </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {painPoints.map((item, idx) => (
                <div 
                  key={item.title} 
                  className="group relative overflow-hidden rounded-2xl border border-slate-100 bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-200 hover:shadow-lg hover:shadow-emerald-500/10"
                >
                  {/* Hover Accent Bar */}
                  <div className="absolute top-0 left-0 h-1 w-full bg-slate-100 transition-colors group-hover:bg-emerald-400" />
                  
                  <div className="mb-2 flex items-center gap-2">
                    {/* Dynamic Icon based on index or just generic */}
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-slate-400 transition-colors group-hover:bg-emerald-100 group-hover:text-emerald-600">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p className="text-sm font-bold text-slate-900 group-hover:text-emerald-800 transition-colors">
                      {item.title}
                    </p>
                  </div>
                  
                  <p className="text-xs leading-relaxed text-slate-500 group-hover:text-slate-600">
                    {item.note}
                  </p>
                </div>
              ))}
            </div>
            
            {/* Background decoration for the grid */}
            <div className="absolute -z-10 -bottom-6 -right-6 h-24 w-24 rounded-full bg-emerald-400/10 blur-xl" />
          </div>

        </div>
      </div>
    </section>
  );
}